// --- Core ---
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {subMinutes} from "date-fns";
import {generatePath} from "react-router";
// --- SaniQ ---
import {fetchAPI} from "api";
import {DeserializedRecord} from "../../../models/Helpers";
import {HandledResponse, handleResponse} from "helpers/Requests";
import {AsyncThunkConfig} from "store";

interface PhysicianResponsibility extends DeserializedRecord<"physician_responsibility"> {
  indirectPatientIds: number[];
  patientIds: number[];
}

interface ResponsibilityState {
  current: PhysicianResponsibility;
  isFetching: boolean;
  lastUpdatedAt: Date;
}

const initialState: ResponsibilityState = {
  current: null,
  isFetching: false,
  lastUpdatedAt: null
};

const fetchResponsibility = createAsyncThunk<HandledResponse<PhysicianResponsibility>, void, AsyncThunkConfig>(
  'responsibility/fetch',
  async (_, thunkAPI) => {
    const {id} = thunkAPI.getState().authContext.login.user;
    const url = generatePath('/web/v1/physicians/:id/responsibility', {id});

    return await fetchAPI(url).then(handleResponse<PhysicianResponsibility>());
  },
  {
    condition: (_, {getState}) => {
      const {authContext: {responsibility}} = getState();

      if (responsibility.isFetching || (responsibility.lastUpdatedAt && responsibility.lastUpdatedAt > subMinutes(new Date(), 5))) {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    }
  }
);

const slice = createSlice({
  // eslint-disable-next-line formatjs/no-literal-string-in-object
  name: 'responsibility',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchResponsibility.pending, (state) => {
      state.isFetching = true;
    });

    builder.addCase(fetchResponsibility.fulfilled, (state, action) => {
      state.current = action.payload.content;
      state.isFetching = false;
      state.lastUpdatedAt = new Date();
    });

    builder.addCase(fetchResponsibility.rejected, (state) => {
      state.current = null;
      state.isFetching = false;
      state.lastUpdatedAt = null;
    });
  }
});

export {fetchResponsibility};

export default slice.reducer;
